// === CHAOS ENGINE ===
//
// The chaos wheel no longer hands out flat point values for Buff/Nerf
// results. Instead every Buff/Nerf reads (and mutates) a persisted snapshot
// of the player's run — `ChaosGameState` — and reacts to whatever has
// actually happened so far.
//
// Adaptation note: the standalone "Magic Gate" wheel was removed from the
// main wheel sequence (Magic is now only reached through the "Magic" ability
// category). Everywhere the original chaos spec refers to "the Magic Gate
// wheel" / "the What Magic wheel", this engine instead looks at
// `state.abilities` for an entry with `category === "Magic"` — that is the
// modern equivalent of "magic has been rolled". The same goes for "Lose Your
// Weapon", which already worked this way (Weapon is an ability category).

import {
  ABILITY_CATEGORY_WHEEL,
  ABILITY_CATEGORY_WHEELS,
  CHAOS_WHEELS,
  TRANSFORMATION_ABILITIES_WHEEL,
  WHEELS,
  Wheel,
  WheelOption,
} from "@/data/wheels";
import { spinWeightedWheel, spinWheel } from "@/lib/rng";
import { SpinResult } from "@/components/ResultsSummary";

// === GAME STATE TYPES ===

export interface WheelResultState {
  spun: boolean;
  label: string;
  points: number;
  lost: boolean;
  halved: boolean;
  doubled: boolean;
  protected: boolean;
  skipped: boolean;
}

export interface AbilityResultState {
  category: string;
  label: string;
  points: number;
  lost: boolean;
  halved: boolean;
  source: "Main" | "Chaos";
}

export interface PowerMasteryState {
  spun: boolean;
  label: string;
  points: number;
}

export interface WeaknessResultState {
  label: string;
  points: number;
  removed: boolean;
  source: string;
}

export type ChaosEventType = "Buff" | "Nerf" | "Lore" | "Achievement";

export interface ChaosEvent {
  type: ChaosEventType;
  result: string;
  pointImpact: number;
  triggeredAfterWheel: string;
  description: string;
  /** True if the effect hasn't fully resolved yet (will apply later). */
  pending?: boolean;
}

export interface ChaosFlags {
  weaponSkipped: boolean;
  magicSkipped: boolean;
  speedHalved: boolean;
  companionSkipped: boolean;
  abilitySystemHalved: boolean;
  weaknessProtected: boolean;
  speciesEvolving: boolean;
  pendingDouble: boolean;
}

export interface ChaosGameState {
  wheels: Record<string, WheelResultState>;
  abilities: AbilityResultState[];
  abilitySystemComplete: boolean;
  abilitySystemHalved: boolean;
  totalAbilityPoints: number;
  powerMastery: PowerMasteryState;
  weaknesses: WeaknessResultState[];
  chaosEvents: ChaosEvent[];
  chaosFlags: ChaosFlags;
}

export function createInitialChaosState(): ChaosGameState {
  return {
    wheels: {},
    abilities: [],
    abilitySystemComplete: false,
    abilitySystemHalved: false,
    totalAbilityPoints: 0,
    powerMastery: { spun: false, label: "", points: 0 },
    weaknesses: [],
    chaosEvents: [],
    chaosFlags: {
      weaponSkipped: false,
      magicSkipped: false,
      speedHalved: false,
      companionSkipped: false,
      abilitySystemHalved: false,
      weaknessProtected: false,
      speciesEvolving: false,
      pendingDouble: false,
    },
  };
}

function emptyWheelState(label: string, points: number): WheelResultState {
  return {
    spun: true,
    label,
    points,
    lost: false,
    halved: false,
    doubled: false,
    protected: false,
    skipped: false,
  };
}

// Chaos state is not persisted on its own — it travels inside the active-run
// snapshot (`ChaosGameState` is a field of `ActiveRunState` in
// `lib/runPersistence.ts`), so there are no standalone save/load helpers here.

// === COOLDOWN / TRIGGER ROLLS ===

export const CHAOS_COOLDOWN_SPINS = 5;
const AUTO_TRIGGER_THRESHOLD = 20; // roll 1-100, <= 20 triggers

// Rolls 1-100; chaos triggers automatically on 20 or below.
export function rollChaosTrigger(): boolean {
  const roll = Math.floor(Math.random() * 100) + 1;
  return roll <= AUTO_TRIGGER_THRESHOLD;
}

export function canManuallyTriggerChaos(spinsSinceLastChaos: number): boolean {
  return spinsSinceLastChaos >= CHAOS_COOLDOWN_SPINS;
}

// "Chaos available in X spins" / "Chaos Ready"
export function getChaosCooldownLabel(spinsSinceLastChaos: number): string {
  const remaining = Math.max(0, CHAOS_COOLDOWN_SPINS - spinsSinceLastChaos);
  if (remaining === 0) return "Chaos Ready";
  return `Chaos available in ${remaining} spin${remaining === 1 ? "" : "s"}`;
}

// === CHAOS TYPE WHEEL (Buff 30 / Nerf 30 / Lore 25 / Achievement 15) ===

export const CHAOS_TYPE_WHEEL: Wheel = {
  id: "chaosType",
  name: "Chaos Type",
  options: [
    { name: "Buff", value: 0, weight: 30 },
    { name: "Nerf", value: 0, weight: 30 },
    { name: "Lore", value: 0, weight: 25 },
    { name: "Achievement", value: 0, weight: 15 },
  ],
};

const NERF_WHEEL = CHAOS_WHEELS.find((w) => w.id === "nerf")!;
const BUFF_WHEEL = CHAOS_WHEELS.find((w) => w.id === "buff")!;
const LORE_WHEEL = CHAOS_WHEELS.find((w) => w.id === "lore")!;
const ACHIEVEMENT_WHEEL = CHAOS_WHEELS.find((w) => w.id === "achievement")!;

const SPECIES_WHEEL = WHEELS.find((w) => w.id === "species")!;
const COMPANION_WHEEL = WHEELS.find((w) => w.id === "companion")!;
const WEAKNESS_WHEEL = WHEELS.find((w) => w.id === "weakness")!;

// === RESULT OF A SINGLE CHAOS EVENT ===

export interface ChaosOutcome {
  state: ChaosGameState;
  event: ChaosEvent;
  /** Net change to the player's total score, applied immediately. */
  scoreDelta: number;
  /** Which existing wheel/ability entry (if any) the UI should highlight. */
  affected?: string;
}

// A wheel is "already affected" once chaos has touched it in any way.
function isWheelAffected(wheel: WheelResultState | undefined): boolean {
  if (!wheel) return false;
  return (
    wheel.lost || wheel.halved || wheel.doubled || wheel.protected || wheel.skipped
  );
}

// === ENTRY POINT ===

// Spins the Chaos Type wheel, then resolves the appropriate Nerf/Buff/Lore/
// Achievement result. Handles the "reroll up to 3 times, then fall back to
// Lore" rule for Nerf/Buff results that would double-affect a wheel.
export function triggerChaosEvent(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome {
  const type = spinWeightedWheel(CHAOS_TYPE_WHEEL).name as ChaosEventType;

  if (type === "Lore") return resolveLore(state, triggeredAfterWheel);
  if (type === "Achievement") return resolveAchievement(state, triggeredAfterWheel);

  const wheel = type === "Nerf" ? NERF_WHEEL : BUFF_WHEEL;
  const resolver = type === "Nerf" ? resolveNerf : resolveBuff;

  for (let attempt = 0; attempt < 3; attempt++) {
    const option = spinWheel(wheel);
    const outcome = resolver(state, option.name, triggeredAfterWheel);
    if (outcome) return outcome;
    // outcome === null means this result would double-affect an
    // already-affected wheel; reroll within the same type.
  }

  // All 3 rerolls hit already-affected wheels — fall back to Lore.
  return resolveLore(state, triggeredAfterWheel);
}

function resolveLore(state: ChaosGameState, triggeredAfterWheel: string): ChaosOutcome {
  const option = spinWheel(LORE_WHEEL);
  const event: ChaosEvent = {
    type: "Lore",
    result: option.name,
    pointImpact: option.value,
    triggeredAfterWheel,
    description: `${option.name} adds to your story (+${option.value.toLocaleString()}).`,
  };
  return {
    state: { ...state, chaosEvents: [...state.chaosEvents, event] },
    event,
    scoreDelta: option.value,
  };
}

function resolveAchievement(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome {
  const option = spinWheel(ACHIEVEMENT_WHEEL);
  const event: ChaosEvent = {
    type: "Achievement",
    result: option.name,
    pointImpact: option.value,
    triggeredAfterWheel,
    description: `You can now say you ${option.name.toLowerCase()} (+${option.value.toLocaleString()}).`,
  };
  return {
    state: { ...state, chaosEvents: [...state.chaosEvents, event] },
    event,
    scoreDelta: option.value,
  };
}

// === NERF RESOLVERS ===
// Each resolver returns null if it would double-affect an already-affected
// wheel, signalling the caller to reroll within the Nerf wheel.

function resolveNerf(
  state: ChaosGameState,
  result: string,
  triggeredAfterWheel: string,
): ChaosOutcome | null {
  switch (result) {
    case "Lose Your Weapon":
      return loseYourWeapon(state, triggeredAfterWheel);
    case "Lose Your Magic":
      return loseYourMagic(state, triggeredAfterWheel);
    case "Halve Your Speed":
      return halveYourSpeed(state, triggeredAfterWheel);
    case "Gain Another Weakness":
      return gainAnotherWeakness(state, triggeredAfterWheel);
    case "Lose Your Companion":
      return loseYourCompanion(state, triggeredAfterWheel);
    case "Powers Malfunction":
      return powersMalfunction(state, triggeredAfterWheel);
    default:
      return null;
  }
}

function loseYourWeapon(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome | null {
  const weaponAbilities = state.abilities.filter(
    (a) => a.category === "Weapon" && !a.lost,
  );

  if (weaponAbilities.length > 0) {
    const best = weaponAbilities.reduce((max, a) =>
      a.points > max.points ? a : max,
    );
    const event: ChaosEvent = {
      type: "Nerf",
      result: "Lose Your Weapon",
      pointImpact: -best.points,
      triggeredAfterWheel,
      description: `${best.label} was lost to chaos.`,
    };
    const abilities = state.abilities.map((a) =>
      a === best ? { ...a, lost: true } : a,
    );
    return {
      state: {
        ...state,
        abilities,
        chaosEvents: [...state.chaosEvents, event],
      },
      event,
      scoreDelta: -best.points,
      affected: best.label,
    };
  }

  if (!state.abilitySystemComplete) {
    // Weapon hasn't been rolled yet — flag it so the ability system can
    // intercept it when (if) the category wheel lands on Weapon.
    if (state.chaosFlags.weaponSkipped) return null; // already pending — reroll
    const event: ChaosEvent = {
      type: "Nerf",
      result: "Lose Your Weapon",
      pointImpact: -500,
      triggeredAfterWheel,
      description:
        "Weapon Stolen Before You Got It — your future weapon roll is voided.",
    };
    return {
      state: {
        ...state,
        chaosFlags: { ...state.chaosFlags, weaponSkipped: true },
        chaosEvents: [...state.chaosEvents, event],
      },
      event,
      scoreDelta: -500,
    };
  }

  // Ability system complete, no weapon was ever rolled.
  const event: ChaosEvent = {
    type: "Nerf",
    result: "Lose Your Weapon",
    pointImpact: -200,
    triggeredAfterWheel,
    description: "You had no weapon to lose.",
  };
  return {
    state: { ...state, chaosEvents: [...state.chaosEvents, event] },
    event,
    scoreDelta: -200,
  };
}

function loseYourMagic(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome | null {
  const magicAbilities = state.abilities.filter(
    (a) => a.category === "Magic" && !a.lost,
  );

  if (magicAbilities.length > 0) {
    const best = magicAbilities.reduce((max, a) =>
      a.points > max.points ? a : max,
    );
    const event: ChaosEvent = {
      type: "Nerf",
      result: "Lose Your Magic",
      pointImpact: -best.points,
      triggeredAfterWheel,
      description: `${best.label} was lost to chaos.`,
    };
    const abilities = state.abilities.map((a) =>
      a === best ? { ...a, lost: true } : a,
    );
    return {
      state: {
        ...state,
        abilities,
        chaosEvents: [...state.chaosEvents, event],
      },
      event,
      scoreDelta: -best.points,
      affected: best.label,
    };
  }

  if (!state.abilitySystemComplete) {
    if (state.chaosFlags.magicSkipped) return null; // already pending — reroll
    const event: ChaosEvent = {
      type: "Nerf",
      result: "Lose Your Magic",
      pointImpact: -1000,
      triggeredAfterWheel,
      description: "Magic Sealed by Chaos — any Magic roll will whiff to nothing.",
    };
    return {
      state: {
        ...state,
        chaosFlags: { ...state.chaosFlags, magicSkipped: true },
        chaosEvents: [...state.chaosEvents, event],
      },
      event,
      scoreDelta: -1000,
    };
  }

  const event: ChaosEvent = {
    type: "Nerf",
    result: "Lose Your Magic",
    pointImpact: -200,
    triggeredAfterWheel,
    description: "You had no magic to lose.",
  };
  return {
    state: { ...state, chaosEvents: [...state.chaosEvents, event] },
    event,
    scoreDelta: -200,
  };
}

function halveYourSpeed(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome | null {
  const speed = state.wheels["Speed"];

  if (speed?.spun) {
    if (isWheelAffected(speed)) return null; // reroll
    const halvedAmount = Math.floor(speed.points / 2);
    const event: ChaosEvent = {
      type: "Nerf",
      result: "Halve Your Speed",
      pointImpact: -halvedAmount,
      triggeredAfterWheel,
      description: `${speed.label} was halved by chaos.`,
    };
    return {
      state: {
        ...state,
        wheels: {
          ...state.wheels,
          Speed: {
            ...speed,
            halved: true,
            points: speed.points - halvedAmount,
          },
        },
        chaosEvents: [...state.chaosEvents, event],
      },
      event,
      scoreDelta: -halvedAmount,
      affected: "Speed",
    };
  }

  // Speed not yet reached — flag it for half-value-on-arrival.
  if (state.chaosFlags.speedHalved) return null; // reroll
  const event: ChaosEvent = {
    type: "Nerf",
    result: "Halve Your Speed",
    pointImpact: 0,
    pending: true,
    triggeredAfterWheel,
    description: "Speed Halved by Chaos — pending, will apply on the Speed spin.",
  };
  return {
    state: {
      ...state,
      chaosFlags: { ...state.chaosFlags, speedHalved: true },
      chaosEvents: [...state.chaosEvents, event],
    },
    event,
    scoreDelta: 0,
  };
}

function gainAnotherWeakness(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome {
  const option = spinWheel(WEAKNESS_WHEEL);
  const weakness: WeaknessResultState = {
    label: option.name,
    points: option.value,
    removed: false,
    source: "Chaos",
  };
  const event: ChaosEvent = {
    type: "Nerf",
    result: "Gain Another Weakness",
    pointImpact: option.value,
    triggeredAfterWheel,
    description: `New weakness — ${option.name} (Chaos Weakness).`,
  };
  return {
    state: {
      ...state,
      weaknesses: [...state.weaknesses, weakness],
      chaosEvents: [...state.chaosEvents, event],
    },
    event,
    scoreDelta: option.value,
    affected: option.name,
  };
}

function loseYourCompanion(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome | null {
  const companion = state.wheels["Companion"];

  if (companion?.spun) {
    if (isWheelAffected(companion)) return null; // reroll

    if (companion.label !== "None" && companion.points > 0) {
      const event: ChaosEvent = {
        type: "Nerf",
        result: "Lose Your Companion",
        pointImpact: -companion.points,
        triggeredAfterWheel,
        description: `${companion.label} was lost to chaos.`,
      };
      return {
        state: {
          ...state,
          wheels: {
            ...state.wheels,
            Companion: { ...companion, lost: true },
          },
          chaosEvents: [...state.chaosEvents, event],
        },
        event,
        scoreDelta: -companion.points,
        affected: "Companion",
      };
    }

    const event: ChaosEvent = {
      type: "Nerf",
      result: "Lose Your Companion",
      pointImpact: -100,
      triggeredAfterWheel,
      description: "You had no companion to lose.",
    };
    return {
      state: {
        ...state,
        wheels: {
          ...state.wheels,
          Companion: { ...companion, lost: true },
        },
        chaosEvents: [...state.chaosEvents, event],
      },
      event,
      scoreDelta: -100,
    };
  }

  // Companion not yet reached — force it to land on "None".
  if (state.chaosFlags.companionSkipped) return null; // reroll
  const event: ChaosEvent = {
    type: "Nerf",
    result: "Lose Your Companion",
    pointImpact: -500,
    pending: true,
    triggeredAfterWheel,
    description: "Companion Claimed by Chaos — your Companion roll is voided.",
  };
  return {
    state: {
      ...state,
      chaosFlags: { ...state.chaosFlags, companionSkipped: true },
      chaosEvents: [...state.chaosEvents, event],
    },
    event,
    scoreDelta: -500,
  };
}

function powersMalfunction(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome | null {
  if (state.abilitySystemComplete) {
    if (state.abilitySystemHalved) return null; // reroll — already halved
    const halvedAmount = Math.floor(state.totalAbilityPoints / 2);
    const event: ChaosEvent = {
      type: "Nerf",
      result: "Powers Malfunction",
      pointImpact: -halvedAmount,
      triggeredAfterWheel,
      description: "Your abilities malfunctioned — 50% of their points are lost.",
    };
    return {
      state: {
        ...state,
        abilitySystemHalved: true,
        totalAbilityPoints: state.totalAbilityPoints - halvedAmount,
        chaosEvents: [...state.chaosEvents, event],
      },
      event,
      scoreDelta: -halvedAmount,
    };
  }

  // Ability system not started/in progress — halve all future gains.
  if (state.chaosFlags.abilitySystemHalved) return null; // reroll
  const event: ChaosEvent = {
    type: "Nerf",
    result: "Powers Malfunction",
    pointImpact: 0,
    pending: true,
    triggeredAfterWheel,
    description:
      "Your powers are unstable — all ability gains will be halved.",
  };
  return {
    state: {
      ...state,
      chaosFlags: { ...state.chaosFlags, abilitySystemHalved: true },
      chaosEvents: [...state.chaosEvents, event],
    },
    event,
    scoreDelta: 0,
  };
}

// === BUFF RESOLVERS ===

function resolveBuff(
  state: ChaosGameState,
  result: string,
  triggeredAfterWheel: string,
): ChaosOutcome | null {
  switch (result) {
    case "Double Your Highest Stat":
      return doubleYourHighestStat(state, triggeredAfterWheel);
    case "Remove One Weakness":
      return removeOneWeakness(state, triggeredAfterWheel);
    case "Gain Another Ability":
      return gainAnotherAbility(state, triggeredAfterWheel);
    case "Gain Another Transformation":
      return gainAnotherTransformation(state, triggeredAfterWheel);
    case "Species Evolves":
      return speciesEvolves(state, triggeredAfterWheel);
    case "Gain Another Companion":
      return gainAnotherCompanion(state, triggeredAfterWheel);
    default:
      return null;
  }
}

function doubleYourHighestStat(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome | null {
  const candidates = Object.entries(state.wheels).filter(
    ([, w]) => w.spun && !isWheelAffected(w),
  );

  if (candidates.length === 0) {
    if (state.chaosFlags.pendingDouble) return null; // reroll — already pending
    const event: ChaosEvent = {
      type: "Buff",
      result: "Double Your Highest Stat",
      pointImpact: 0,
      pending: true,
      triggeredAfterWheel,
      description:
        "Double Pending — will apply to your highest roll at the end of the run.",
    };
    return {
      state: {
        ...state,
        chaosFlags: { ...state.chaosFlags, pendingDouble: true },
        chaosEvents: [...state.chaosEvents, event],
      },
      event,
      scoreDelta: 0,
    };
  }

  const [name, best] = candidates.reduce((max, entry) =>
    entry[1].points > max[1].points ? entry : max,
  );
  const event: ChaosEvent = {
    type: "Buff",
    result: "Double Your Highest Stat",
    pointImpact: best.points,
    triggeredAfterWheel,
    description: `${name} Doubled: +${best.points.toLocaleString()}.`,
  };
  return {
    state: {
      ...state,
      wheels: { ...state.wheels, [name]: { ...best, doubled: true } },
      chaosEvents: [...state.chaosEvents, event],
    },
    event,
    scoreDelta: best.points,
    affected: name,
  };
}

function removeOneWeakness(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome {
  const negativeWeaknesses = state.weaknesses.filter(
    (w) => w.points < 0 && !w.removed,
  );

  if (negativeWeaknesses.length > 0) {
    const worst = negativeWeaknesses.reduce((min, w) =>
      w.points < min.points ? w : min,
    );
    const restored = Math.abs(worst.points);
    const event: ChaosEvent = {
      type: "Buff",
      result: "Remove One Weakness",
      pointImpact: restored,
      triggeredAfterWheel,
      description: `${worst.label} was removed by chaos.`,
    };
    const weaknesses = state.weaknesses.map((w) =>
      w === worst ? { ...w, removed: true } : w,
    );
    return {
      state: { ...state, weaknesses, chaosEvents: [...state.chaosEvents, event] },
      event,
      scoreDelta: restored,
      affected: worst.label,
    };
  }

  const weaknessWheelSpun = state.wheels["Weakness"]?.spun;

  if (!weaknessWheelSpun) {
    // Weakness wheel not reached yet — protect it in advance.
    const event: ChaosEvent = {
      type: "Buff",
      result: "Remove One Weakness",
      pointImpact: 0,
      pending: true,
      triggeredAfterWheel,
      description:
        "Protected by Chaos — any negative Weakness roll will be negated.",
    };
    return {
      state: {
        ...state,
        chaosFlags: { ...state.chaosFlags, weaknessProtected: true },
        chaosEvents: [...state.chaosEvents, event],
      },
      event,
      scoreDelta: 0,
    };
  }

  // Weakness wheel already landed on "None" (+3000) — bonus free spin.
  const option = spinWheel(WEAKNESS_WHEEL);
  if (option.value < 0) {
    // Free pass — immediately remove it, net 0.
    const weakness: WeaknessResultState = {
      label: option.name,
      points: option.value,
      removed: true,
      source: "Chaos",
    };
    const event: ChaosEvent = {
      type: "Buff",
      result: "Remove One Weakness",
      pointImpact: 0,
      triggeredAfterWheel,
      description: `Rolled ${option.name} and immediately removed it — free pass.`,
    };
    return {
      state: {
        ...state,
        weaknesses: [...state.weaknesses, weakness],
        chaosEvents: [...state.chaosEvents, event],
      },
      event,
      scoreDelta: 0,
      affected: option.name,
    };
  }

  // Landed on "None" — award normally.
  const weakness: WeaknessResultState = {
    label: option.name,
    points: option.value,
    removed: false,
    source: "Chaos",
  };
  const event: ChaosEvent = {
    type: "Buff",
    result: "Remove One Weakness",
    pointImpact: option.value,
    triggeredAfterWheel,
    description: `Bonus weakness roll: ${option.name}.`,
  };
  return {
    state: {
      ...state,
      weaknesses: [...state.weaknesses, weakness],
      chaosEvents: [...state.chaosEvents, event],
    },
    event,
    scoreDelta: option.value,
    affected: option.name,
  };
}

function gainAnotherAbility(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome {
  const category = spinWeightedWheel(ABILITY_CATEGORY_WHEEL);
  const categoryWheel = ABILITY_CATEGORY_WHEELS[category.name];
  const ability = spinWheel(categoryWheel);

  let points = ability.value;
  let halvedNote = "";
  if (state.chaosFlags.abilitySystemHalved || state.abilitySystemHalved) {
    points = Math.floor(points / 2);
    halvedNote = " (halved by Powers Malfunction)";
  }

  const entry: AbilityResultState = {
    category: category.name,
    label: ability.name,
    points,
    lost: false,
    halved: !!(state.chaosFlags.abilitySystemHalved || state.abilitySystemHalved),
    source: "Chaos",
  };

  const event: ChaosEvent = {
    type: "Buff",
    result: "Gain Another Ability",
    pointImpact: points,
    triggeredAfterWheel,
    description: `Chaos Ability — ${ability.name} (${category.name})${halvedNote}.`,
  };

  return {
    state: {
      ...state,
      abilities: [...state.abilities, entry],
      totalAbilityPoints: state.totalAbilityPoints + points,
      chaosEvents: [...state.chaosEvents, event],
    },
    event,
    scoreDelta: points,
    affected: ability.name,
  };
}

function gainAnotherTransformation(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome {
  const result = spinWheel(TRANSFORMATION_ABILITIES_WHEEL);

  // Transformations stack (a character can hold several forms), but the roll
  // is compared against what's already owned before deciding how to apply it:
  const existing = state.abilities.filter(
    (a) => a.category === "Transformation" && !a.lost,
  );
  const duplicate = existing.find((a) => a.label === result.name);

  // Replace-with-identical: rolling a form you already have grants nothing
  // (no farming the same transformation for repeat points), net zero.
  if (duplicate) {
    const event: ChaosEvent = {
      type: "Buff",
      result: "Gain Another Transformation",
      pointImpact: 0,
      triggeredAfterWheel,
      description: `Chaos Transformation — ${result.name} (already owned, no change).`,
    };
    return {
      state: { ...state, chaosEvents: [...state.chaosEvents, event] },
      event,
      scoreDelta: 0,
    };
  }

  // New, distinct form — stack it on top of any existing transformations.
  const entry: AbilityResultState = {
    category: "Transformation",
    label: result.name,
    points: result.value,
    lost: false,
    halved: false,
    source: "Chaos",
  };

  const event: ChaosEvent = {
    type: "Buff",
    result: "Gain Another Transformation",
    pointImpact: result.value,
    triggeredAfterWheel,
    description: `Chaos Transformation — ${result.name}.`,
  };

  return {
    state: {
      ...state,
      abilities: [...state.abilities, entry],
      totalAbilityPoints: state.totalAbilityPoints + result.value,
      chaosEvents: [...state.chaosEvents, event],
    },
    event,
    scoreDelta: result.value,
    affected: result.name,
  };
}

// Tiered species progression used by "Species Evolves". Tied tiers (two
// names at the same power level) pick randomly between the two options.
const SPECIES_TIERS: string[][] = [
  ["Human"],
  ["Mutant"],
  ["Cyborg"],
  ["Alien"],
  ["Vampire", "Werewolf"],
  ["Angel", "Demon"],
  ["Dragonkin"],
  ["Eldritch Being"],
  ["God"],
];

function speciesPoints(name: string): number {
  return SPECIES_WHEEL.options.find((o) => o.name === name)?.value ?? 0;
}

function nextSpeciesTierName(current: string): string | null {
  const tierIndex = SPECIES_TIERS.findIndex((tier) => tier.includes(current));
  if (tierIndex === -1 || tierIndex === SPECIES_TIERS.length - 1) return null;
  const nextTier = SPECIES_TIERS[tierIndex + 1];
  return nextTier[Math.floor(Math.random() * nextTier.length)];
}

function speciesEvolves(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome | null {
  const species = state.wheels["Species"];

  if (species?.spun) {
    if (isWheelAffected(species)) return null; // reroll — already evolved

    const nextName = nextSpeciesTierName(species.label);
    if (nextName === null) {
      // Already at God — beyond-evolution bonus instead.
      const event: ChaosEvent = {
        type: "Buff",
        result: "Species Evolves",
        pointImpact: 500,
        triggeredAfterWheel,
        description: "Beyond Evolution — already a God, +500 bonus.",
      };
      return {
        state: {
          ...state,
          wheels: { ...state.wheels, Species: { ...species, doubled: true } },
          chaosEvents: [...state.chaosEvents, event],
        },
        event,
        scoreDelta: 500,
        affected: "Species",
      };
    }

    const oldPoints = species.points;
    const newPoints = speciesPoints(nextName);
    const delta = newPoints - oldPoints;
    const event: ChaosEvent = {
      type: "Buff",
      result: "Species Evolves",
      pointImpact: delta,
      triggeredAfterWheel,
      description: `Species Evolved: ${species.label} → ${nextName}.`,
    };
    return {
      state: {
        ...state,
        wheels: {
          ...state.wheels,
          Species: {
            ...species,
            label: nextName,
            points: newPoints,
            doubled: true, // marks this wheel as already chaos-affected
          },
        },
        chaosEvents: [...state.chaosEvents, event],
      },
      event,
      scoreDelta: delta,
      affected: "Species",
    };
  }

  // Species not yet reached — auto-upgrade whatever it lands on.
  if (state.chaosFlags.speciesEvolving) return null; // reroll
  const event: ChaosEvent = {
    type: "Buff",
    result: "Species Evolves",
    pointImpact: 0,
    pending: true,
    triggeredAfterWheel,
    description: "Evolved on Arrival — your Species roll will be upgraded one tier.",
  };
  return {
    state: {
      ...state,
      chaosFlags: { ...state.chaosFlags, speciesEvolving: true },
      chaosEvents: [...state.chaosEvents, event],
    },
    event,
    scoreDelta: 0,
  };
}

function gainAnotherCompanion(
  state: ChaosGameState,
  triggeredAfterWheel: string,
): ChaosOutcome {
  const result = spinWheel(COMPANION_WHEEL);

  const entry: AbilityResultState = {
    category: "Companion",
    label: result.name,
    points: result.value,
    lost: false,
    halved: false,
    source: "Chaos",
  };

  const event: ChaosEvent = {
    type: "Buff",
    result: "Gain Another Companion",
    pointImpact: result.value,
    triggeredAfterWheel,
    description: `Chaos Companion — ${result.name}.`,
  };

  return {
    state: {
      ...state,
      abilities: [...state.abilities, entry],
      totalAbilityPoints: state.totalAbilityPoints + result.value,
      chaosEvents: [...state.chaosEvents, event],
    },
    event,
    scoreDelta: result.value,
    affected: result.name,
  };
}

// === SPIN-TIME EFFECT APPLICATION ===
//
// Called when a main wheel result lands, BEFORE it's recorded into
// `state.wheels`. Applies any pending chaos flags (skip/halve/protect/evolve)
// for that specific wheel and returns the effective option plus a visual hint
// for the UI.

export type ChaosSpinVisual =
  | "skipped"
  | "halved"
  | "protected"
  | "evolved"
  | null;

export interface ChaosAdjustedSpin {
  option: WheelOption;
  visual: ChaosSpinVisual;
  // The chaos state with the pending flag cleared and its event resolved (no
  // longer "pending"). The caller records the wheel result on top of THIS state.
  state: ChaosGameState;
}

// Marks the first still-pending chaos event with this result name as resolved:
// clears its `pending` flag and (optionally) sets its real point impact. This is
// display-only — the score change rides on the modified wheel result, never on
// chaosEvents — so it just stops the on-wheel panel from showing "Pending"
// forever once the effect has actually landed.
function resolvePendingEvent(
  state: ChaosGameState,
  result: string,
  pointImpact?: number,
): ChaosGameState {
  let done = false;
  const chaosEvents = state.chaosEvents.map((e) => {
    if (!done && e.pending && e.result === result) {
      done = true;
      return {
        ...e,
        pending: false,
        pointImpact: pointImpact ?? e.pointImpact,
      };
    }
    return e;
  });
  return { ...state, chaosEvents };
}

export function applyPendingSpinEffects(
  state: ChaosGameState,
  wheelName: string,
  rolledOption: WheelOption,
): ChaosAdjustedSpin {
  if (wheelName === "Speed" && state.chaosFlags.speedHalved) {
    const value = Math.floor(rolledOption.value / 2);
    const cleared = {
      ...state,
      chaosFlags: { ...state.chaosFlags, speedHalved: false },
    };
    return {
      option: { ...rolledOption, value },
      visual: "halved",
      state: resolvePendingEvent(
        cleared,
        "Halve Your Speed",
        value - rolledOption.value,
      ),
    };
  }

  if (wheelName === "Companion" && state.chaosFlags.companionSkipped) {
    const none = COMPANION_WHEEL.options.find((o) => o.name === "None")!;
    const cleared = {
      ...state,
      chaosFlags: { ...state.chaosFlags, companionSkipped: false },
    };
    // The penalty was already booked when this fired (-500 in chaosResults);
    // just clear the flag and stop the event showing as pending.
    return {
      option: none,
      visual: "skipped",
      state: resolvePendingEvent(cleared, "Lose Your Companion"),
    };
  }

  if (wheelName === "Weakness" && state.chaosFlags.weaknessProtected) {
    const cleared = {
      ...state,
      chaosFlags: { ...state.chaosFlags, weaknessProtected: false },
    };
    if (rolledOption.value < 0) {
      return {
        option: { ...rolledOption, value: 0 },
        visual: "protected",
        // The buff saved you the negative roll.
        state: resolvePendingEvent(
          cleared,
          "Remove One Weakness",
          -rolledOption.value,
        ),
      };
    }
    return {
      option: rolledOption,
      visual: "protected",
      state: resolvePendingEvent(cleared, "Remove One Weakness", 0),
    };
  }

  if (wheelName === "Species" && state.chaosFlags.speciesEvolving) {
    const cleared = {
      ...state,
      chaosFlags: { ...state.chaosFlags, speciesEvolving: false },
    };
    const nextName = nextSpeciesTierName(rolledOption.name);
    if (nextName) {
      const value = speciesPoints(nextName);
      return {
        option: { name: nextName, value },
        visual: "evolved",
        state: resolvePendingEvent(
          cleared,
          "Species Evolves",
          value - rolledOption.value,
        ),
      };
    }
    return {
      option: rolledOption,
      visual: "evolved",
      state: resolvePendingEvent(cleared, "Species Evolves", 0),
    };
  }

  return { option: rolledOption, visual: null, state };
}

// Called when the ability category wheel lands on "Weapon" or "Magic" while
// chaosFlags.weaponSkipped / magicSkipped is set — the roll is voided.
export function isAbilityCategorySkipped(
  state: ChaosGameState,
  category: string,
): boolean {
  if (category === "Weapon" && state.chaosFlags.weaponSkipped) return true;
  if (category === "Magic" && state.chaosFlags.magicSkipped) return true;
  return false;
}

// Halves ability/mastery points if Powers Malfunction is pending.
export function applyAbilityHalving(
  state: ChaosGameState,
  points: number,
): number {
  return state.chaosFlags.abilitySystemHalved ? Math.floor(points / 2) : points;
}

// === RECORDING RESULTS INTO STATE ===

export function recordWheelResult(
  state: ChaosGameState,
  wheelName: string,
  label: string,
  points: number,
): ChaosGameState {
  return {
    ...state,
    wheels: { ...state.wheels, [wheelName]: emptyWheelState(label, points) },
  };
}

export function recordAbilityResult(
  state: ChaosGameState,
  category: string,
  label: string,
  points: number,
): ChaosGameState {
  const halved = state.chaosFlags.abilitySystemHalved;
  const effectivePoints = applyAbilityHalving(state, points);
  const entry: AbilityResultState = {
    category,
    label,
    points: effectivePoints,
    lost: false,
    halved,
    source: "Main",
  };
  return {
    ...state,
    abilities: [...state.abilities, entry],
    abilitySystemHalved: halved || state.abilitySystemHalved,
    totalAbilityPoints: state.totalAbilityPoints + effectivePoints,
  };
}

export function recordWeakness(
  state: ChaosGameState,
  label: string,
  points: number,
  source: string,
): ChaosGameState {
  return {
    ...state,
    weaknesses: [...state.weaknesses, { label, points, removed: false, source }],
  };
}

export function recordPowerMastery(
  state: ChaosGameState,
  label: string,
  points: number,
): ChaosGameState {
  const effectivePoints = applyAbilityHalving(state, points);
  let next: ChaosGameState = {
    ...state,
    abilitySystemComplete: true,
    powerMastery: { spun: true, label, points: effectivePoints },
    totalAbilityPoints: state.totalAbilityPoints + effectivePoints,
  };
  // The ability system is now complete; a pending Powers Malfunction has fully
  // taken effect (every gain was halved), so it's no longer "pending".
  if (state.chaosFlags.abilitySystemHalved) {
    next = resolvePendingEvent(next, "Powers Malfunction");
  }
  return next;
}

// === RUN-END RETROACTIVE EFFECTS ===

// Resolves chaosFlags.pendingDouble (set when "Double Your Highest Stat"
// fired before any wheel had been spun): doubles whatever ended up being the
// single highest-value wheel result.
export function finalizePendingDouble(state: ChaosGameState): ChaosOutcome | null {
  if (!state.chaosFlags.pendingDouble) return null;

  const candidates = Object.entries(state.wheels).filter(
    ([, w]) => w.spun && !isWheelAffected(w),
  );
  if (candidates.length === 0) return null;

  const [name, best] = candidates.reduce((max, entry) =>
    entry[1].points > max[1].points ? entry : max,
  );

  const event: ChaosEvent = {
    type: "Buff",
    result: "Double Your Highest Stat",
    pointImpact: best.points,
    triggeredAfterWheel: "Final Results",
    description: `${name} Doubled (retroactive): +${best.points.toLocaleString()}.`,
  };

  // Resolve the existing pending "Double" event rather than appending a second
  // one, so the panel shows the real value instead of a lingering "Pending".
  return {
    state: resolvePendingEvent(
      {
        ...state,
        wheels: { ...state.wheels, [name]: { ...best, doubled: true } },
        chaosFlags: { ...state.chaosFlags, pendingDouble: false },
      },
      "Double Your Highest Stat",
      best.points,
    ),
    event,
    scoreDelta: best.points,
    affected: name,
  };
}

// === FINAL SUMMARY HELPERS ===

export interface ChaosSummary {
  events: ChaosEvent[];
  totalGained: number;
  totalLost: number;
  netImpact: number;
}

export function summarizeChaosEvents(state: ChaosGameState): ChaosSummary {
  let totalGained = 0;
  let totalLost = 0;
  for (const event of state.chaosEvents) {
    if (event.pointImpact > 0) totalGained += event.pointImpact;
    else if (event.pointImpact < 0) totalLost += event.pointImpact;
  }
  return {
    events: state.chaosEvents,
    totalGained,
    totalLost,
    netImpact: totalGained + totalLost,
  };
}

// === APPLYING A RESOLVED CHAOS OUTCOME TO THE RESULTS LISTS ===
//
// Whenever possible, a chaos event's score impact is folded directly into
// the wheel/ability result it affects (re-labelled to show what chaos did),
// rather than appearing as its own separate line. Only "new" results (a
// fresh ability/weakness/companion, Lore, an Achievement, or a standalone
// penalty with nothing to attach to) get their own entry in `chaosResults`.

function updateFirstMatch(
  list: SpinResult[],
  predicate: (r: SpinResult) => boolean,
  update: (r: SpinResult) => SpinResult,
): { list: SpinResult[]; found: boolean } {
  let found = false;
  const next = list.map((r) => {
    if (!found && predicate(r)) {
      found = true;
      return update(r);
    }
    return r;
  });
  return { list: next, found };
}

export function applyChaosOutcomeEffects(
  outcome: ChaosOutcome,
  currentResults: SpinResult[],
  currentChaosResults: SpinResult[],
): { results: SpinResult[]; chaosResults: SpinResult[] } {
  const { event, state, affected } = outcome;
  let results = currentResults;
  let chaosResults = currentChaosResults;
  let absorbed = false;

  switch (event.result) {
    case "Halve Your Speed": {
      if (affected === "Speed") {
        const speed = state.wheels["Speed"];
        const { list, found } = updateFirstMatch(
          results,
          (r) => r.wheelName === "Speed",
          (r) => ({
            wheelName: r.wheelName,
            option: { name: `${speed.label} (Halved by Chaos)`, value: speed.points },
          }),
        );
        results = list;
        absorbed = found;
      }
      break;
    }
    case "Lose Your Companion": {
      if (affected === "Companion") {
        const { list, found } = updateFirstMatch(
          results,
          (r) => r.wheelName === "Companion",
          (r) => ({
            wheelName: r.wheelName,
            option: { name: `${r.option.name} (Lost to Chaos)`, value: 0 },
          }),
        );
        results = list;
        absorbed = found;
      }
      break;
    }
    case "Species Evolves": {
      if (affected === "Species") {
        const species = state.wheels["Species"];
        const { list, found } = updateFirstMatch(
          results,
          (r) => r.wheelName === "Species",
          () => ({
            wheelName: "Species",
            option: {
              name: `${species.label} (Evolved by Chaos)`,
              value: species.points,
            },
          }),
        );
        results = list;
        absorbed = found;
      }
      break;
    }
    case "Double Your Highest Stat": {
      if (affected) {
        const { list, found } = updateFirstMatch(
          results,
          (r) => r.wheelName === affected,
          (r) => ({
            wheelName: r.wheelName,
            option: {
              name: `${r.option.name} (Doubled by Chaos)`,
              value: r.option.value * 2,
            },
          }),
        );
        results = list;
        absorbed = found;
      }
      break;
    }
    case "Lose Your Weapon":
    case "Lose Your Magic": {
      if (affected) {
        const { list, found } = updateFirstMatch(
          results,
          (r) => r.wheelName.startsWith("Ability:") && r.option.name === affected,
          (r) => ({
            wheelName: r.wheelName,
            option: { name: `${r.option.name} (Lost to Chaos)`, value: 0 },
          }),
        );
        results = list;
        absorbed = found;
      }
      break;
    }
    case "Remove One Weakness": {
      if (affected) {
        const inResults = updateFirstMatch(
          results,
          (r) =>
            r.wheelName === "Weakness" && r.option.name === affected && r.option.value < 0,
          (r) => ({
            wheelName: r.wheelName,
            option: { name: `${r.option.name} (Removed by Chaos)`, value: 0 },
          }),
        );
        if (inResults.found) {
          results = inResults.list;
          absorbed = true;
        } else {
          const inChaos = updateFirstMatch(
            chaosResults,
            (r) => r.option.name === affected && r.option.value < 0,
            (r) => ({
              wheelName: r.wheelName,
              option: { name: `${r.option.name} (Removed by Chaos)`, value: 0 },
            }),
          );
          if (inChaos.found) {
            chaosResults = inChaos.list;
            absorbed = true;
          }
        }
      }
      break;
    }
    default:
      break;
  }

  // Standalone effects (new abilities/weaknesses/companions, Lore,
  // Achievements, or penalties with nothing to attach to) get their own
  // entry. Zero-impact pending flags are already logged in
  // `state.chaosEvents` and don't need a visible row of their own.
  if (!absorbed && event.pointImpact !== 0) {
    chaosResults = [
      ...chaosResults,
      { wheelName: event.type, option: { name: event.result, value: event.pointImpact } },
    ];
  }

  return { results, chaosResults };
}
